import * as React from "react";
import { useKernelClient } from "./useKernelClient";
import { useUserOperationRunner, ContractCall } from "./useUserOperationRunner";
import { encodeFunctionData, Hex } from "viem";

interface WalletToken {
    batchData: { abi: any[]; functionName: string; args: any[]; };
}
export interface UseMigrationProps {
    dotNetRef: any;
    walletTokensList: WalletToken[];
    contractAddress: Hex;
    bundlerRpc: string;
    paymasterRpc: string;
    isDevelopment?: boolean;
    selectedPrivyWalletAddress?: string;
}

export const useBurnIslandMigration = (props: UseMigrationProps) => {
    const { dotNetRef, walletTokensList, contractAddress, bundlerRpc, paymasterRpc, isDevelopment, selectedPrivyWalletAddress } = props;

    const { client, isInitializing: isClientInitializing, error: clientError } = useKernelClient({ 
        bundlerRpc, 
        paymasterRpc, 
        isDevelopment,
        selectedWalletAddress: selectedPrivyWalletAddress 
    });
    const { execute, isExecuting, error: runnerError, completedTxs } = useUserOperationRunner(client, dotNetRef);
    
    const [isComplete, setIsComplete] = React.useState(false);

    const stableWalletTokensList = React.useMemo(() => walletTokensList, [JSON.stringify(walletTokensList)]);

    const executeMigration = React.useCallback(() => {
        const calls: ContractCall[] = stableWalletTokensList.map(token => ({
            to: contractAddress,
            value: BigInt(0),
            data: encodeFunctionData({ ...token.batchData }),
        }));
        execute(calls);
    }, [stableWalletTokensList, contractAddress, execute]);

    // Effect to handle completion and send the final callback to .NET
    React.useEffect(() => {
        const error = clientError || runnerError;
        if (!isExecuting && (completedTxs.length > 0 || error) && !isComplete) {
            const result = {
                type: "complete", // Specify the event type
                success: !error,
                transactionHash: completedTxs.length > 0 ? completedTxs[completedTxs.length - 1] : null,
                error: error,
            };
            dotNetRef.invokeMethodAsync("OnEventCallback", JSON.stringify(result));
            setIsComplete(true);
        }
    }, [isExecuting, completedTxs, clientError, runnerError, dotNetRef, isComplete]);
    
    // Automatically execute when the client is ready
    React.useEffect(() => {
        if (!isClientInitializing && client) {
            executeMigration();
        }
    }, [isClientInitializing, client, executeMigration]);


    return {
        isInitializing: isClientInitializing,
        isExecuting,
        isComplete,
        error: clientError || runnerError,
    };
}; 